<script setup>
// GK-HUB: Inbox list — card grid with branded icons + deactivation toggle
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const loading = ref({});

// GK-HUB: Active/Inactive tabs
const showInactive = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

// GK-HUB: Branded background color per channel type for the icon circle
const iconBgClass = inbox => {
  const map = {
    'Channel::Whatsapp': 'bg-[#25D366]/10',
    'Channel::Instagram': 'bg-[#E1306C]/10',
    'Channel::Telegram': 'bg-[#26A5E4]/10',
    'Channel::FacebookPage': 'bg-[#0084FF]/10',
    'Channel::Line': 'bg-[#06C755]/10',
    'Channel::Email': 'bg-[#D14836]/10',
    'Channel::WebWidget': 'bg-n-blue-3',
    'Channel::Api': 'bg-n-alpha-2',
    'Channel::Sms': 'bg-n-alpha-2',
    'Channel::TwilioSms': 'bg-n-alpha-2',
    'Channel::Tiktok': 'bg-n-alpha-2',
    'Channel::Voice': 'bg-n-alpha-2',
    'Channel::TwitterProfile': 'bg-n-alpha-2',
  };
  return map[inbox.channel_type] || 'bg-n-alpha-2';
};
// GK-HUB: end iconBgClass

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

// GK-HUB: Separate active/inactive lists
const activeInboxes = computed(() =>
  (inboxesList.value || []).filter(i => !i.deactivated_at)
);
const inactiveInboxes = computed(() =>
  (inboxesList.value || []).filter(i => i.deactivated_at)
);
const displayedInboxes = computed(() =>
  showInactive.value ? inactiveInboxes.value : activeInboxes.value
);
// GK-HUB: end

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

// GK-HUB: Toggle inbox active/inactive
const toggleInboxStatus = async inbox => {
  loading.value[inbox.id] = true;
  try {
    await store.dispatch('inboxes/delete', inbox.id);
    const msg = inbox.deactivated_at
      ? t('INBOX_MGMT.ACTIVATE.API.SUCCESS_MESSAGE')
      : t('INBOX_MGMT.DEACTIVATE.API.SUCCESS_MESSAGE');
    useAlert(msg);
  } catch (error) {
    useAlert(t('INBOX_MGMT.DEACTIVATE.API.ERROR_MESSAGE'));
  }
  loading.value[inbox.id] = false;
};
// GK-HUB: end toggle
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <!-- GK-HUB: card grid with active/inactive tabs -->
    <template #body>
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <div class="flex items-center gap-3 mb-4">
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          :class="
            !showInactive
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="showInactive = false"
        >
          {{ $t('INBOX_MGMT.LIST_TABS.ACTIVE') }}
          <span class="ml-1 text-xs opacity-70"
            >({{ activeInboxes.length }})</span
          >
        </button>
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          :class="
            showInactive
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="showInactive = true"
        >
          {{ $t('INBOX_MGMT.LIST_TABS.INACTIVE') }}
          <span class="ml-1 text-xs opacity-70"
            >({{ inactiveInboxes.length }})</span
          >
        </button>
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template -->

      <p v-if="!displayedInboxes.length" class="text-sm text-n-slate-9 py-4">
        {{
          showInactive
            ? $t('INBOX_MGMT.LIST_TABS.NO_INACTIVE')
            : $t('INBOX_MGMT.LIST.404')
        }}
      </p>

      <div
        v-else
        class="grid max-w-5xl grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-5"
      >
        <div
          v-for="inbox in displayedInboxes"
          :key="inbox.id"
          class="relative bg-n-solid-1 rounded-2xl border border-solid border-n-weak cursor-pointer transition-all duration-200 ease-in hover:border-n-blue-9 hover:shadow-md group"
          :class="{ 'opacity-50': inbox.deactivated_at }"
          @click="
            $router.push({
              name: 'settings_inbox_show',
              params: { inboxId: inbox.id },
            })
          "
        >
          <div class="flex flex-col items-start gap-3 py-5 px-5">
            <!-- Icon -->
            <div
              class="flex size-10 items-center justify-center rounded-full"
              :class="iconBgClass(inbox)"
            >
              <ChannelIcon class="size-5" :inbox="inbox" />
            </div>
            <!-- Name + Channel -->
            <div class="flex flex-col gap-0.5 min-w-0 w-full">
              <h3
                class="text-n-slate-12 text-sm font-medium capitalize leading-tight truncate"
              >
                {{ inbox.name }}
                <!-- GK-HUB: inactive badge -->
                <span
                  v-if="inbox.deactivated_at"
                  class="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-ruby-500/10 text-ruby-500 font-normal"
                >
                  {{ $t('INBOX_MGMT.STATUS.INACTIVE') }}
                </span>
              </h3>
              <ChannelName
                class="text-xs text-n-slate-10"
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
              />
            </div>
          </div>
          <!-- GK-HUB: Toggle activate/deactivate button (admin only, hover) -->
          <Button
            v-if="isAdmin"
            v-tooltip.top="
              inbox.deactivated_at
                ? $t('INBOX_MGMT.ACTIVATE.BUTTON_TEXT')
                : $t('INBOX_MGMT.DEACTIVATE.BUTTON_TEXT')
            "
            :icon="
              inbox.deactivated_at ? 'i-lucide-power' : 'i-lucide-power-off'
            "
            xs
            faded
            :class="
              inbox.deactivated_at
                ? 'text-green-600 hover:bg-green-500/10'
                : 'text-ruby-500 hover:bg-ruby-500/10'
            "
            class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
            :is-loading="loading[inbox.id]"
            @click.stop="toggleInboxStatus(inbox)"
          />
          <!-- GK-HUB: end toggle button -->
        </div>
      </div>
    </template>
    <!-- GK-HUB: end card grid -->
  </SettingsLayout>
</template>
